/**
 * ⟦mensaje-de-error⟧ **EL CUERPO DE UN `4xx`/`5xx`, LEÍDO UNA SOLA VEZ.**
 *
 * La reparación de `H-AUD-003` (`D.4` del plan del 26/8). El hallazgo, textual:
 * *«La rama `if (!respuesta.ok)` nunca lee `respuesta.json()`. Construye el
 * mensaje de error sólo con el código de estado HTTP, y descarta el cuerpo de la
 * respuesta — que es justamente donde `admin-web` puso el texto útil.»*
 *
 * `gates/cuerpo-de-error.spec.ts` `CE3` mide que ese cuerpo es `{error: <texto>}`
 * en **54/54** rutas de `apps/admin-web` y **8/8** de `apps/bot-web`, sin una
 * excepción. El texto útil viaja por el cable y llega hasta acá; lo único que
 * faltaba era leerlo.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐ POR QUÉ ESTE ARCHIVO EXISTE, Y POR QUÉ NO CONSOLIDA `errorDelBorde`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Los quince sitios reparables viven en doce archivos, y cada uno de esos doce
 * trae su propia copia de `errorDelBorde` (once definiciones idénticas; el
 * doceavo, `configuracion.ts`, tiene su propia clase de error). **Esa
 * duplicación se deja como está y la nueva no se agrega**, y el criterio que
 * separa los dos casos es cuánto se puede equivocar cada uno:
 *
 *   · `errorDelBorde` es `Object.assign(new Error(m), { statusCode })`. No tiene
 *     ninguna decisión adentro, no puede degradar mal, y un clon que se
 *     desincronice no produce ningún síntoma. Consolidarlo tocaría once
 *     archivos para cambiar cero comportamientos — un refactor que nadie pidió,
 *     en una rama donde hay otros agentes en vuelo.
 *   · **Lo de acá abajo sí tiene decisiones**: cinco formas distintas de cuerpo
 *     que no cumple el contrato, y el modo de falla más caro —un `4xx` que pase
 *     como éxito, o un `.error` que reviente el `catch` que la pantalla ya
 *     tiene— está a un `(await r.json()).error` de distancia. `CE2` del gate
 *     existe exactamente para eso: está VERDE hoy porque el código nunca lee el
 *     cuerpo, y su trabajo es seguir verde después de la reparación. Quince
 *     copias de esta escalera son quince oportunidades de escribirla mal y
 *     quince lugares donde arreglarla.
 *
 * ⇒ **se duplica lo que no puede fallar, y se comparte lo que sí.**
 *
 * ════════════════════════════════════════════════════════════════════════════
 * LO QUE ESTE MÓDULO **NO** HACE
 * ════════════════════════════════════════════════════════════════════════════
 *
 *   · **No decide si lanzar.** El llamador ya decidió que la rama es de error;
 *     acá sólo se arma el texto. Un cuerpo ilegible devuelve el mensaje genérico
 *     y el llamador lanza igual — nunca se convierte un `4xx` en un éxito.
 *   · **No mira el `Content-Type`.** A propósito: un borde que contesta
 *     `text/html` con un JSON adentro, o `application/json` con la página de
 *     error de un proxy, son los dos casos reales. Se intenta parsear el texto y
 *     se decide por lo que hay, no por lo que el header promete.
 *   · **No traduce ni reescribe el texto del backend.** `ADR-0201` es del lado
 *     del productor —el texto de un cuerpo de error es constante en tiempo de
 *     compilación— y este módulo lo transporta tal cual. Que ese texto sea
 *     legible para un operador no técnico es el pendiente ⑤ del ADR (dueño:
 *     BID/PM) y no se resuelve acá.
 */

// ════════════════════════════════════════════════════════════════════════════
// ⭐⭐ `R2-UI6` · **401 → LOGIN, 403 → DENEGADO, 409 → ELEGIR CONTRASEÑA.
//     EN UN SOLO LUGAR, Y ES ÉSTE.** `// ADR-0353`
// ════════════════════════════════════════════════════════════════════════════
//
// `CA-F6-55` y `CA-F6-57` piden que un `403`/`401` de **CUALQUIER** borde —no de
// uno— mande a la pantalla de acceso denegado o al login. `F6.md` §10 riesgo 13
// lo llama «en UN solo lugar», y `r2-ui6.md:49` nombra como ese lugar *«el
// `queryClient` de Refine/`react-query` que `App.tsx:142-144` monta»*.
//
// ⛔⛔ **ESE `queryClient` NO EXISTE, Y AUNQUE EXISTIERA NO ALCANZARÍA.** Medido
// el 22/9/2026 sobre el árbol: `grep -rn "QueryClient" apps/management-ui/src` da
// **cero** (las tres menciones son de comentarios), `App.tsx:140-144` son
// imports, y el `dataProvider` de Refine sirve **un solo recurso**
// (`RECURSO_DOCUMENTOS`) ⇒ engancharse ahí cubriría **1 de 14 bordes**: las otras
// trece pantallas tienen su propio `fetch`. Un punto único que cubre 1 de 14 no
// es un punto único. La decisión y su medición están en `ADR-0353` §D1.
//
// ⭐ **El único punto por el que pasan los trece bordes es esta función.**
// Medido el 22/9/2026:
//
//   · **21 ramas `if (!X.ok)`** en `src/**`. **20 son de una respuesta HTTP y las
//     20 llaman a `mensajeDeError`.** La veintiuna (`borde-escritura.ts:338`,
//     `!lectura.ok`) no es HTTP: es la validación de FORMA de un JSON ya parseado,
//     lanza `502` a mano y no puede ser un 401 ni un 403.
//   · 15 archivos la importan, 20 llamadas.
//
// ⇒ **cobertura del gancho sobre las ramas de error HTTP: 20/20.** No hay
// agujero que declarar; hay dos EXENCIONES, y están abajo.
//
// ⛔ **Y por qué acá y no en cada borde:** poner `if (statusCode === 401)
// navigate(…)` en cada `borde-*.ts` es tocar **doce territorios** en la wave
// exclusiva de `ui-marco`. «Que cada pantalla decida» es exactamente cómo
// `carga-en-lote/pantalla-carga.tsx:361` terminó tratando el 401 como «no se pudo
// cargar».

/**
 * Quién reacciona a un código de sesión. Lo registra el `ProveedorDeSesion`
 * (`sesion.tsx`) al montarse, y lo desregistra al desmontarse.
 *
 * ⛔ **Módulo, y no un contexto de React, a propósito.** Los bordes son funciones
 * `async` sueltas —no componentes— y no pueden leer un contexto. El estado vive
 * acá, que es el único lugar que las dos puntas comparten.
 *
 * ⛔ **`null` mientras nadie la registre, y eso es lo normal**: las pruebas de
 * unidad de este módulo y los exámenes que montan una pantalla sola no registran
 * nada, y `mensajeDeError` sigue siendo la función PURA que era. Es opt-IN para
 * quien la registra y **transparente para quien no**.
 */
type ReaccionDeSesion = (statusCode: number) => void

let reaccionDeSesion: ReaccionDeSesion | null = null

/**
 * Registra —o borra, con `null`— la reacción central a los códigos de sesión.
 *
 * ⚠️ **Devuelve la que estaba.** El `ProveedorDeSesion` la restituye al
 * desmontarse: sin eso, un examen que monta y desmonta la app dos veces dejaría
 * apuntando a la instancia MUERTA de la primera, y el síntoma sería una
 * navegación que «no pasa» en la segunda.
 */
export function registrarReaccionDeSesion(reaccion: ReaccionDeSesion | null): ReaccionDeSesion | null {
  const anterior = reaccionDeSesion
  reaccionDeSesion = reaccion
  return anterior
}

/**
 * Lo que un llamador puede pedirle a `mensajeDeError` que NO haga.
 *
 * ⛔⛔ **Es OPT-OUT, no opt-in, y la diferencia es la propiedad entera.** Con
 * opt-in, un borde nuevo que se olvide de pedirlo queda mudo y nadie se entera:
 * `CA-F6-55` fallaría para esa pantalla sola, con `tsc` en cero y el resto en
 * verde. Con opt-out, el que se olvida queda CUBIERTO, y quien necesita quedar
 * afuera tiene que escribirlo y decir por qué — que es lo que hace `configuracion.ts`.
 */
export interface OpcionesDeMensaje {
  /**
   * `true` ⇒ este `4xx` **no** es un código de sesión, y no dispara la reacción
   * central. **Las dos exenciones declaradas, y sus motivos:**
   *
   *   ① **`login/borde-sesion.ts` — exenta POR CONSTRUCCIÓN, sin bandera.** El
   *      rechazo de login (`:188`) y el de elegir contraseña (`:225`) **no pasan
   *      por acá**: usan `textoDelRechazo` → `motivoDelCuerpo`, porque
   *      `CA-F6-07/08/20` piden igualdad EXACTA con `SIGN_IN_REJECTED_COPY` y
   *      `mensajeDeError` le pegaría el genérico atrás. ⇒ **el 401 del propio
   *      login no se puede volver una navegación al login**, que es justo lo que
   *      haría falta evitar. Está escrito acá porque desde `borde-sesion.ts` —que
   *      es territorio de `R2-UI5` y esta etapa no puede editar— no se ve, y
   *      alguien podría «uniformarlo» a `mensajeDeError` sin saber que así se
   *      rompen tres criterios y se fabrica un lazo.
   *      ⚠️ El `!ok` de `quienSoy()` (`:204`) **sí** pasa por acá, y está bien: su
   *      401 ya salió antes por `return null`, así que lo que llegue es un 5xx.
   *
   *   ② **`configuracion.ts:756`** — lo usa esta bandera. Ver ahí el motivo.
   */
  readonly noEsCodigoDeSesion?: boolean
}

/**
 * Lo que separa el motivo del backend del mensaje genérico del borde.
 *
 * El motivo va PRIMERO porque es lo que el operador necesita para corregir, y el
 * genérico queda de contexto para quien tenga que reportar el problema. El gate
 * permite las dos órdenes (`CE1·d`: *«acompañar el motivo con el código está
 * PERMITIDO»*); la que se elige es la que sirve sin leer hasta el final.
 */
const SEPARADOR = " · "

/**
 * Un motivo más largo que esto no es un motivo: es una página, un stack o un
 * volcado. Se corta para que no reviente el modal que lo tiene que mostrar
 * (`H-AUD-005` es ese síntoma, del otro lado).
 */
const LARGO_MAXIMO = 400

/**
 * El texto que el backend puso en `{error: …}`, o `null` si no hay ninguno.
 *
 * ⛔ **Nunca tira.** El criterio es el que el hallazgo cita y que
 * `leerRestricciones` ya aplica en `borde-escritura.ts`: no reventar si el
 * proveedor no cumple el contrato. Las cinco formas que `CE2` mide caen todas
 * en un `return null`:
 *
 *   | el cuerpo …                          | acá                         |
 *   |--------------------------------------|-----------------------------|
 *   | no es JSON (`<html>502…`)            | `JSON.parse` tira ⇒ `null`  |
 *   | es JSON sin la clave `error`         | `error` es `undefined`      |
 *   | tiene `error` que no es texto        | `typeof !== "string"`       |
 *   | tiene `error` vacío                  | se descarta al recortar     |
 *   | está vacío                           | se descarta antes de parsear|
 *   | es JSON pero no un objeto (`[1,2,3]`)| `Array.isArray` / `typeof`  |
 *
 * Y el `Content-Type` que miente no es un caso aparte porque nunca se lo mira.
 */
export async function motivoDelCuerpo(respuesta: Response): Promise<string | null> {
  let crudo: string
  try {
    /**
     * `.text()` y no `.json()`: `.json()` tira con un cuerpo que no es JSON, y
     * ahí ya se perdió la posibilidad de mirar qué vino. Además el cuerpo se
     * consume UNA sola vez, así que un `.json()` que falla deja la respuesta sin
     * cuerpo para el intento siguiente.
     *
     * El `try` cubre también el cuerpo que se corta a mitad de la lectura: un
     * `Response` cuyo stream falle tira acá y no en la pantalla.
     */
    crudo = await respuesta.text()
  } catch {
    return null
  }

  if (crudo.trim() === "") return null

  let cuerpo: unknown
  try {
    cuerpo = JSON.parse(crudo)
  } catch {
    return null
  }

  /**
   * `typeof null === "object"` y `typeof [] === "object"`: los dos se descartan
   * explícitamente. Un `[1,2,3]` con la propiedad `error` no existe, pero
   * `("error" in cuerpo)` sobre un arreglo no tira y devolvería `false` — se
   * prefiere que el motivo del descarte esté escrito.
   */
  if (typeof cuerpo !== "object" || cuerpo === null || Array.isArray(cuerpo)) return null

  const motivo = (cuerpo as { readonly error?: unknown }).error
  if (typeof motivo !== "string") return null

  const recortado = motivo.trim()
  if (recortado === "") return null

  return recortado.length > LARGO_MAXIMO ? `${recortado.slice(0, LARGO_MAXIMO)}…` : recortado
}

/**
 * El mensaje que la pantalla va a mostrar por una respuesta que no es `ok`.
 *
 * `generico` es el mensaje que el borde venía armando solo —`admin-web respondió
 * 400.`, `bot-web respondió 500 al leer las entregas.`— y sigue siendo la caída:
 * cuando el cuerpo no sirve, lo que se ve es exactamente lo de antes de la
 * reparación. Cuando sirve, el operador lee primero qué corregir.
 *
 * ⚠️ **Consume el cuerpo de `respuesta`.** Se llama sólo en la rama de error,
 * donde el llamador está por lanzar y ya no lo va a leer.
 */
export async function mensajeDeError(
  respuesta: Response,
  generico: string,
  opciones?: OpcionesDeMensaje,
): Promise<string> {
  /*
   * ⭐ `R2-UI6` · la reacción se dispara **ANTES de leer el cuerpo**, y no es un
   * detalle de orden: `motivoDelCuerpo` consume el stream y puede tardar. Que la
   * navegación no dependa de que el cuerpo se pueda parsear es la diferencia
   * entre `CA-F6-57` cumpliéndose siempre y cumpliéndose cuando el proxy manda
   * JSON.
   *
   * ⛔ **Y el mensaje se arma IGUAL.** La reacción no corta el flujo: el borde
   * sigue lanzando su `errorDelBorde` con el texto de siempre, y la pantalla
   * sigue pintando su cartel. `r2-ui6.md:49`: *«el mensaje de error que ya
   * construyen se conserva»*. Si la navegación efectivamente ocurre, ese cartel
   * se desmonta con la pantalla; si no ocurre —porque el 401 llegó de algo que no
   * es la sesión— el operador ve lo de antes en vez de una pantalla en blanco.
   */
  if (opciones?.noEsCodigoDeSesion !== true) reaccionDeSesion?.(respuesta.status)

  const motivo = await motivoDelCuerpo(respuesta)
  return motivo === null ? generico : `${motivo}${SEPARADOR}${generico}`
}
